import { MetalsTaskType } from '../enums/metals-task-type.enum';

/**
 * Metals assembly quoting — the board model.
 *
 * A board is a flow chart of parts. Each node is one thing that gets made or
 * bought: a sheet metal part, a purchased piece, a weldment, an assembly. Links
 * run child -> parent with a quantity per parent, so an assembly is just a node
 * that other nodes feed. Each node carries the operations it goes through
 * (laser, press brake, welding, powder coating ...) with setup and per-part
 * minutes, and cost is always recipe x the board's rates — nothing is stored.
 *
 * Prototype scope: one board, no variations, no quote lifecycle. Persistence
 * is the browser for now; the shapes here are what the collections will hold.
 */

export interface MetalsAssemblyQuoteOperation {
    id: string;
    type: MetalsTaskType;
    /** one-time minutes per run — spread across the quantity being made */
    setupMinutes: number;
    minutesPerPart: number;
    /** a flat per-part charge: how outside processing is priced; zero for shop ops */
    costPerPart: number;
}

/** what the raw stock is: a sheet or plate blank, a cut of tube, a cut of bar or billet, or just a weight */
export type MetalsAssemblyQuoteStockForm = 'sheet' | 'tube' | 'bar' | 'weight';

/** the section of a tube or bar */
export type MetalsAssemblyQuoteStockProfile = 'round' | 'square' | 'rectangular';

/**
 * The raw material one part is cut from. The weight is derived from the
 * dimensions and the family's density for every form but `weight`, which is
 * for stock the estimator already knows the weight of. Fields a form does not
 * use are simply ignored, so switching forms keeps what was typed.
 */
export interface MetalsAssemblyQuoteMaterial {
    /** key into rates.pricePerLbByFamily; null when the node has no raw material of its own */
    family: string | null;
    form: MetalsAssemblyQuoteStockForm;
    /** tube and bar only */
    profile: MetalsAssemblyQuoteStockProfile;
    /** sheet: the sheet thickness; tube: the wall */
    thicknessIn: number;
    /** sheet: blank length; tube and bar: the cut length */
    lengthIn: number;
    /** sheet: blank width; rectangular tube and bar: the second side */
    widthIn: number;
    /** tube: outside diameter or the first side; bar: diameter or the first side */
    sizeIn: number;
    /** weight form only: the pounds per part, typed */
    weightLb: number;
}

/** a bought-in item fitted to the node: qty per part x unit cost */
export interface MetalsAssemblyQuoteHardwareRow {
    id: string;
    description: string;
    quantity: number;
    unitCost: number;
}

export interface MetalsAssemblyQuoteNode {
    id: string;
    partNumber: string;
    description: string;
    /** place on the board */
    x: number;
    y: number;
    material: MetalsAssemblyQuoteMaterial;
    /**
     * A picture of the part for the card and the drawer. A data URL while the
     * board lives in the browser; a storage URL once it has collections.
     */
    imageUrl: string | null;
    /**
     * Which product names in the CAD assembly this card stands for, comma
     * separated, instance and solid suffixes ignored. Blank means the part
     * number. "FrameTube24, FrameTube25" lets one card cover a LH/RH pair.
     */
    cadName: string;
    /** in process order */
    operations: MetalsAssemblyQuoteOperation[];
    hardware: MetalsAssemblyQuoteHardwareRow[];
}

/** child -> parent: `quantity` of `from` go into one `to` */
export interface MetalsAssemblyQuoteLink {
    id: string;
    from: string;
    to: string;
    quantity: number;
}

export interface MetalsAssemblyQuoteRates {
    /** shop rate per operation type, $/hr */
    hourlyRateByTaskType: Record<MetalsTaskType, number>;
    /** $/lb by material family (Mild, Stainless, Galvanized, Aluminum ...) */
    pricePerLbByFamily: Record<string, number>;
}

export interface MetalsAssemblyQuoteBoard {
    name: string;
    /** how many of each top-level deliverable the price is for */
    quantity: number;
    marginPercent: number;
    /** the assembly STEP loaded for the review; the file itself is kept beside the board */
    stepFileName: string | null;
    nodes: MetalsAssemblyQuoteNode[];
    links: MetalsAssemblyQuoteLink[];
    rates: MetalsAssemblyQuoteRates;
}

/** per-part cost of one node at one quantity */
export interface MetalsAssemblyQuoteCostBreakdown {
    material: number;
    /** shop operations: (setup / qty + per part) minutes at the op's hourly rate */
    labor: number;
    /** flat per-part charges on operations (outside processing) */
    outside: number;
    hardware: number;
    /** linked children: qty per parent x the child's per-part cost at its own quantity */
    components: number;
    total: number;
}

export const METALS_ASSEMBLY_QUOTE_EMPTY_BREAKDOWN: Readonly<MetalsAssemblyQuoteCostBreakdown> = Object.freeze({
    material: 0, labor: 0, outside: 0, hardware: 0, components: 0, total: 0,
});

/** everything the cost math reads */
export interface MetalsAssemblyQuoteCostingContext {
    nodes: readonly MetalsAssemblyQuoteNode[];
    links: readonly MetalsAssemblyQuoteLink[];
    rates: MetalsAssemblyQuoteRates;
}
